import * as React from 'react';
import { translate } from 'react-i18next';
import { IStore } from 'redux/IStore';
const { connect } = require('react-redux');
import * as c from 'models/constants';
import { browserHistory, RouteComponentProps } from 'react-router';
import { resetData } from 'redux/modules/shop/shopcard';
import { IShopDashboardData } from 'models/shop/dashboard';
import { getSales, getBookingTemplatesPopularity, setPeriodicity } from 'redux/modules/shop/dashboard';
import { PortalSelectMenu } from 'components/FormControls/PortalSelectMenu';
import { currentOffice } from '../Helpers';
import ShopInfoGraph, { IGraphData } from '../ShopInfoGraph';
import { ITranslateProps } from 'models/common';
import { Periodicity } from 'models/enums/periodicity';
import { Dispatch } from 'react-redux';
import { getAppointmentStatistics } from 'redux/modules/shop/appointments';
import { formatCurrency, formatCurrencyToParts, getCurrencySymbol } from 'helpers/common';
import { momentWithTimezone } from 'redux/modules/shop/helpers';

const formatPrice = (currencyKey, value, key) => {
	const parts = formatCurrencyToParts(currencyKey, value);

	return (
		<>{parts.map((p) => (typeof p !== 'string' && p?.type === 'currency' ? p.value : <span key={key}>{p}</span>))}</>
	);
};

interface IParams {
	id?: string;
	officeId?: string;
}

interface IProps
	extends RouteComponentProps<IParams, {}>,
		ReturnType<typeof mapStateToProps>,
		ReturnType<typeof mapDispatchToProps>,
		ITranslateProps {}

const mapStateToProps = (state: IStore) => ({
	shopCard: state.shopcard.shopcard,
	shopDashboardData: state.shopDashboard, // Used for periodicity, sales and servicePopularity
	appointments: state.appointments, // Used for appointmentStatistics object inside it
});

const mapDispatchToProps = (dispatch: Dispatch<IStore>) => ({
	resetData: () => dispatch(resetData()),
	getSales: (shopId: string, officeId?: string, shouldSetDate?: boolean) =>
		dispatch(getSales(shopId, officeId, shouldSetDate)),
	getBookingTemplatesPopularity: (shopId: string, officeId?: string) =>
		dispatch(getBookingTemplatesPopularity(shopId, officeId)),
	getAppointmentStatistics: (shopId: string, period: Periodicity, officeId?: string, timezone?: string) =>
		dispatch(getAppointmentStatistics(shopId, period, officeId, timezone)),
	setPeriodicity: (periodicity: Periodicity) => dispatch(setPeriodicity(periodicity, 'analytics')),
});

@translate(c.DEFAULT_TRANSLATION_NAMESPACE)
class ShopInfoAnalyticsForm extends React.Component<IProps, {}> {
	constructor(props) {
		super(props);
		this.handleAnalyticsLocationChange = this.handleAnalyticsLocationChange.bind(this);
		this.handlePeriodFilterClick = this.handlePeriodFilterClick.bind(this);
		this.loadData = this.loadData.bind(this);
	}

	private loadData() {
		const shopId = this.props.params.id;
		const officeId = this.props.params.officeId;

		////////////////////////////////////
		// get all sales data for analytics
		////////////////////////////////////

		if (shopId) {
			if (officeId && officeId !== 'all') {
				// If selected office
				this.props.getSales(shopId, officeId, false); // Always get AllTime
				this.props.getBookingTemplatesPopularity(shopId, officeId); // Get for current period
			} else {
				// If all offices
				this.props.getSales(shopId, undefined, false); // Always get AllTime
				this.props.getBookingTemplatesPopularity(shopId); // Get for current period
			}

			const timezone = this.props.shopCard?.offices?.find((x) => x.id === officeId)?.zoneInfo;
			// There must be 4 calls. There is no way around.
			this.props.getAppointmentStatistics(shopId, Periodicity.Week, officeId, timezone);
			this.props.getAppointmentStatistics(shopId, Periodicity.Month, officeId, timezone);
			this.props.getAppointmentStatistics(shopId, Periodicity.Year, officeId, timezone);
			this.props.getAppointmentStatistics(shopId, Periodicity.AllTime, officeId, timezone);
			////////////////////////////////////
		}
	}

	componentDidMount() {
		this.props.setPeriodicity(Periodicity.Week); // Set redux state's shopDashboard > selectedPeridiocity and dateFrom / dateTo
		this.loadData();
	}

	componentDidUpdate(prevProps: Readonly<IProps>): void {
		// Load new data when shopId or officeId has changed
		if (this.props.params.id !== prevProps.params.id || this.props.params.officeId !== prevProps.params.officeId) {
			this.loadData();
		}
	}
	componentWillUnmount() {
		this.props.resetData();
	}

	/////////////////////////////////////////////////////////////
	// onClick actions
	/////////////////////////////////////////////////////////////
	private handleAnalyticsLocationChange(officeId: string) {
		const shopId = this.props.params.id;
		browserHistory.push(`/shop/${shopId}/edit/analytics/${officeId}`);
	}

	private handlePeriodFilterClick(period: Periodicity) {
		this.props.setPeriodicity(period); // Set redux state's shopDashboard > selectedPeridiocity and dateFrom / dateTo
		this.props.getBookingTemplatesPopularity(this.props.params.id, this.props.params.officeId); // Get for new period on click
	}

	render() {
		if (typeof window === 'undefined') {
			return null;
		}

		const { shopCard, t, appointments } = this.props;
		const offices = shopCard?.offices;
		const officeId = this.props.params.officeId;
		const shopId = this.props.params.id;
		const timezone = offices?.find((x) => x.id === officeId)?.zoneInfo;

		///////////////////////////////////////
		// Helpers
		///////////////////////////////////////
		// const { selectedPeriodFilter } = this.state;
		const { selectedPeriodicity } = this.props.shopDashboardData;
		const getTotal = (
			period: typeof selectedPeriodicity,
		): {
			selectedTotal: number;
			selectedOrders: IShopDashboardData['sales'];
			numberOfServices: number;
		} => {
			let selectedTotal = 0;
			const selectedOrders = [];
			if (this.props.shopDashboardData?.sales?.length) {
				// This week
				if (period === 0) {
					this.props.shopDashboardData.sales
						.filter(
							(x) =>
								momentWithTimezone(timezone, x.date) >= momentWithTimezone(timezone).startOf('isoWeek') &&
								momentWithTimezone(timezone, x.date) <= momentWithTimezone(timezone).endOf('isoWeek'),
						)
						.forEach((x) => {
							selectedTotal += x.total;
							selectedOrders.push(x);
						});
					// This month
				} else if (period === 1) {
					this.props.shopDashboardData.sales
						.filter((x) => {
							const choice =
								momentWithTimezone(timezone, x.date) >= momentWithTimezone(timezone).startOf('month') &&
								momentWithTimezone(timezone, x.date) <= momentWithTimezone(timezone).endOf('month');
							return choice;
						})
						.forEach((x) => {
							selectedTotal += x.total;
							selectedOrders.push(x);
						});
					// This year
				} else if (period === 2) {
					this.props.shopDashboardData.sales
						.filter(
							(x) =>
								momentWithTimezone(timezone, x.date) >= momentWithTimezone(timezone).startOf('year') &&
								momentWithTimezone(timezone, x.date) <= momentWithTimezone(timezone).endOf('year'),
						)
						.forEach((x) => {
							selectedTotal += x.total;
							selectedOrders.push(x);
						});
					// All time
				} else if (period === 3) {
					this.props.shopDashboardData.sales.forEach((x) => {
						selectedTotal += x.total;
						selectedOrders.push(x);
					});
				}
			}
			return {
				selectedTotal,
				selectedOrders,
				numberOfServices: selectedOrders.length,
			};
		};
		///////////////////////////////////////
		// TODO REMOVE DUMMY DATA
		///////////////////////////////////////
		const prepareGraphData = (): {
			earnings: IGraphData['data'];
			appointments: IGraphData['data'];
		} => {
			let earnings: IGraphData['data'] = [];
			const appointments: IGraphData['data'] = [];
			// const selectedPeriod = this.state.selectedPeriodFilter;
			const selectedPeriod = this.props.shopDashboardData?.selectedPeriodicity;
			const selectedOrders = getTotal(selectedPeriod).selectedOrders;
			let formatString = '';

			const generateData = (
				x: string,
			): {
				earnings: IGraphData['data'];
				appointments: IGraphData['data'];
			} => {
				let existingDates: string[] = [];
				/////////////////////////////////////////////////////////
				// fill all x-values for selected period
				/////////////////////////////////////////////////////////
				// today's date used for reference
				const date = new Date();
				// This week
				if (selectedPeriod === 0) {
					const startOfWeekDate = momentWithTimezone(timezone, date).startOf('isoWeek');
					existingDates = Array(7)
						.fill(0)
						.map((_, index) => momentWithTimezone(timezone, startOfWeekDate).add(index, 'days').format(x));
					// This month
				} else if (selectedPeriod === 1) {
					const startOfMonthDate = momentWithTimezone(timezone, date).startOf('month');
					existingDates = Array(startOfMonthDate.daysInMonth())
						.fill(0)
						.map((_, index) => momentWithTimezone(timezone, startOfMonthDate).add(index, 'days').format(x));
					// This year
				} else if (selectedPeriod === 2) {
					const startOfYearDate = momentWithTimezone(timezone, date).startOf('year');
					existingDates = Array(12)
						.fill(0)
						.map((_, index) => momentWithTimezone(timezone, startOfYearDate).add(index, 'months').format(x));
					// All time
				} else if (selectedPeriod === 3) {
					const allYears: number[] = [];
					selectedOrders.forEach((x) => {
						if (!allYears.find((y) => y === momentWithTimezone(timezone, x.date).year())) {
							allYears.push(momentWithTimezone(timezone, x.date).year());
						}
					});

					allYears.sort((a, b) => {
						return a - b;
					});
					// add extra years before and after
					allYears.unshift(allYears[0] - 1);
					allYears.push(allYears[allYears.length - 1] + 1);

					existingDates = Array(allYears.length)
						.fill(0)
						.map((_, index) => momentWithTimezone(timezone, new Date(allYears[0], 0)).add(index, 'years').format(x));
				}
				/////////////////////////////////////////////////////////
				// Fill all value-pairs with zeros for y, because we might have days with 0 revenue
				existingDates.forEach((x, index) => {
					earnings[index] = {
						x,
						y: 0,
					};
					appointments[index] = {
						x,
						y: 0,
					};
				});
				selectedOrders.forEach((order) => {
					// combine values of orders with the same x-value (day, month, year)
					earnings.forEach((dataObj, index) => {
						if (dataObj.x === momentWithTimezone(timezone, order.date).format(x)) {
							earnings[index] = {
								...earnings[index],
								y: earnings[index].y + order.total,
							};
						}
					});
					// combine values of appointments with the same x-value (day, month, year)
					appointments.forEach((dataObj, index) => {
						if (dataObj.x === momentWithTimezone(timezone, order.date).format(x)) {
							appointments[index] = {
								...appointments[index],
								y: appointments[index].y + 1,
							};
						}
					});
				});

				return {
					earnings,
					appointments,
				};
			};

			// This week
			if (selectedPeriod === 0) {
				formatString = 'DD MMM';
				earnings = generateData(formatString).earnings;
				// This month
			} else if (selectedPeriod === 1) {
				formatString = 'DD';
				earnings = generateData(formatString).earnings;
				// This year
			} else if (selectedPeriod === 2) {
				formatString = 'MMM';
				earnings = generateData(formatString).earnings;
				// All time
			} else if (selectedPeriod === 3) {
				formatString = 'YYYY';
				earnings = generateData(formatString).earnings;
			}
			return {
				earnings,
				appointments,
			};
		};

		const salesData: IGraphData[] = [
			{
				id: 'Sales',
				color: 'hsl(278, 70%, 50%)',
				data: prepareGraphData().earnings,
			},
		];
		const appointmentsData: IGraphData[] = [
			{
				id: 'Appointments',
				color: 'hsl(278, 70%, 50%)',
				data: prepareGraphData().appointments,
			},
		];
		/////////////////////////////////////////////////////////////////////////
		// Helpers
		/////////////////////////////////////////////////////////////////////////
		const renderShopStatElement = (value: number | JSX.Element, title: string, value2?: string): JSX.Element => {
			return (
				<div className="shopStatsElement">
					{
						<p>
							<span>{value}</span>
						</p>
					}
					<p>{`${title.toUpperCase()}`}</p>
					{value2 ? (
						<>
							<p>
								<span>{value !== 0 ? value2 + ' %' : '0.0 %'}</span>
							</p>
							<p>{`${t('User_ShopCard_Occupancy').toUpperCase()}`}</p>
						</>
					) : null}
				</div>
			);
		};
		/////////////////////////////////////////////////////////////////////////
		// Header
		/////////////////////////////////////////////////////////////////////////
		const AnalyticsHeader = (): JSX.Element => {
			let items: { key: string; value: string }[] = [];
			if (shopCard?.offices) {
				items = shopCard.offices.map((x) => ({ key: x.id, value: x.location }));
				items.unshift({ key: 'all', value: t('BSPortal_Common_AllLocations') });
			}
			return (
				<div className="analyticsHeaderContainer row">
					{shopCard?.offices ? (
						<div className="officeSelector col-lg-6 col-md-12">
							<PortalSelectMenu
								usage="portal"
								input={{
									value: currentOffice(offices, officeId, shopId) ? currentOffice(offices, officeId, shopId).id : 'all',
									onChange: this.handleAnalyticsLocationChange,
								}}
								items={items}
								label={t('Shop_ManageShop_Location_Label_SalonLocation')}
								searchable={false}
							/>
						</div>
					) : (
						<div className="officeSelector col-lg-6 col-md-12">
							<PortalSelectMenu
								usage="portal"
								input={{
									value: 'all',
									onChange: () => null,
								}}
								items={[{ key: 'all', value: 'all' }]}
								label={t('Shop_ManageShop_Location_Label_SalonLocation')}
								searchable={false}
							/>
						</div>
					)}
					<div className="periodFilterContainer col-lg-6 col-md-12 d-flex justify-content-end">
						<button type="button" className={setActive(0)} onClick={() => this.handlePeriodFilterClick(0)}>
							<p className="p14">{`1 ${t('Periodselector_Periodicity_1').charAt(0).toUpperCase()}`}</p>
						</button>
						<button type="button" className={setActive(1)} onClick={() => this.handlePeriodFilterClick(1)}>
							<p className="p14">{`1 ${t('Periodselector_Periodicity_2').charAt(0).toUpperCase()}`}</p>
						</button>
						<button type="button" className={setActive(2)} onClick={() => this.handlePeriodFilterClick(2)}>
							<p className="p14">{`1 ${t('Periodselector_Periodicity_3').charAt(0).toUpperCase()}`}</p>
						</button>
						<button type="button" className={setActive(3)} onClick={() => this.handlePeriodFilterClick(3)}>
							<p className="p14">{`${t('BSPortal_Common_All').toUpperCase()}`}</p>
						</button>
					</div>
				</div>
			);
		};
		const setActive = (period: Periodicity): string => {
			if (this.props.shopDashboardData?.selectedPeriodicity === period) {
				return 'active';
			}
			return '';
		};
		/////////////////////////////////////////////////////////////////////////
		// Appointment Earning section
		/////////////////////////////////////////////////////////////////////////
		const ShopSalesStats = (): JSX.Element => {
			return (
				<div className="shopStatsContainer">
					{renderShopStatElement(
						formatPrice(this.props.shopCard?.currencyKey, getTotal(0).selectedTotal, 11),
						t('Periodselector_Periodicity_1'),
					)}
					{renderShopStatElement(
						formatPrice(this.props.shopCard?.currencyKey, getTotal(1).selectedTotal, 12),
						t('Periodselector_Periodicity_2'),
					)}
					{renderShopStatElement(
						formatPrice(this.props.shopCard?.currencyKey, getTotal(2).selectedTotal, 13),
						t('Periodselector_Periodicity_3'),
					)}
					{renderShopStatElement(
						formatPrice(this.props.shopCard?.currencyKey, getTotal(3).selectedTotal, 14),
						t('Periodselector_Periodicity_4'),
					)}
				</div>
			);
		};
		/////////////////////////////////////////////////////////////////////////
		// Service Popularity section
		/////////////////////////////////////////////////////////////////////////
		const ShopServicePopularity = (): JSX.Element => {
			const columnWidths = ['1', '7', '2', '2'];
			const headerTitles = [
				'#',
				t('User_ShopCard_Analytics_Service'),
				t('User_ShopCard_Analytics_Bookings'),
				t('User_ShopCard_Analytics_Revenue') + ' ' + `(${getCurrencySymbol(shopCard?.currencyKey)})`,
			];
			const renderHeader = (): JSX.Element => {
				return (
					<div className="shopServicePopularityHeader row">
						<div className={`col-${columnWidths[0]}`}>
							<p className="p14">{headerTitles[0].toUpperCase()}</p>
						</div>
						<div className={`col-${columnWidths[1]}`}>
							<p className="p14">{headerTitles[1].toUpperCase()}</p>
						</div>
						<div className={`col-${columnWidths[2]}`}>
							<p className="p14 right">{headerTitles[2].toUpperCase()}</p>
						</div>
						<div className={`col-${columnWidths[3]}`}>
							<p className="p14 right">{headerTitles[3].toUpperCase()}</p>
						</div>
					</div>
				);
			};

			const renderElements = (): JSX.Element[] => {
				const btData = this.props?.shopDashboardData?.servicePopularity || [];

				return btData.map((el, index) => {
					return (
						<div className="shopServicePopularityelement row" key={index}>
							<div className={`col-${columnWidths[0]}`}>
								<p className="p14">{index + 1}</p>
							</div>
							<div className={`col-${columnWidths[1]}`}>
								<p className="p14">{el.name}</p>
							</div>
							<div className={`col-${columnWidths[2]}`}>
								<p className="p14 right">{el.count}</p>
							</div>
							<div className={`col-${columnWidths[3]}`}>
								<p className="p14 right">{formatCurrency(shopCard?.currencyKey, el.total)}</p>
							</div>
						</div>
					);
				});
			};

			return (
				<div className="shopServicePopularity">
					{renderHeader()}
					{renderElements()}
				</div>
			);
		};
		/////////////////////////////////////////////////////////////////////////
		// Appointments section
		/////////////////////////////////////////////////////////////////////////
		const ShopAppointmentsStats = (): JSX.Element => {
			return (
				<div className="shopStatsContainer">
					{renderShopStatElement(
						getTotal(0).numberOfServices,
						t('Periodselector_Periodicity_1'),
						(appointments?.appointmentStatistics?.thisWeek?.occupancy * 100).toFixed(1),
					)}
					{renderShopStatElement(
						getTotal(1).numberOfServices,
						t('Periodselector_Periodicity_2'),
						(appointments?.appointmentStatistics?.thisMonth?.occupancy * 100).toFixed(1),
					)}
					{renderShopStatElement(
						getTotal(2).numberOfServices,
						t('Periodselector_Periodicity_3'),
						(appointments?.appointmentStatistics?.thisYear?.occupancy * 100).toFixed(1),
					)}
					{renderShopStatElement(
						getTotal(3).numberOfServices,
						t('Periodselector_Periodicity_4'),
						(appointments?.appointmentStatistics?.allTime?.occupancy * 100).toFixed(1),
					)}
				</div>
			);
		};
		/////////////////////////////////////////////////////////////////////////

		return (
			<div className="shopAnalytics container">
				<AnalyticsHeader />
				<h1>{t('User_ShopCard_Analytics_AppointmentEarnings')}</h1>
				<ShopSalesStats />
				<ShopInfoGraph currencyKey={shopCard?.currencyKey} data={salesData} type="sales" />
				<h1>{t('User_ShopCard_Analytics_ServicesPopularity')}</h1>
				<ShopServicePopularity />
				<h1>{t('User_UserCard_Appointments')}</h1>
				<ShopAppointmentsStats />
				<ShopInfoGraph currencyKey={shopCard?.currencyKey} data={appointmentsData} type="appointments" />
			</div>
		);
	}
}

const ShopInfoAnalytics = connect(mapStateToProps, mapDispatchToProps)(ShopInfoAnalyticsForm);

export default ShopInfoAnalytics;
